/**
 * Admin API Hooks
 *
 * Custom hooks for calling admin Cloud Functions.
 */

import { useState, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../lib/firebase';

// Re-export helper functions for convenience
export {
  formatRelativeTime,
  formatCurrency,
  formatCompactNumber,
  formatNumber,
  formatProcessingTime,
} from '../utils/dateHelpers';

/**
 * Generic hook for calling admin functions with loading/error state
 */
export function useAdminFunction<TRequest, TResponse>(
  functionName: string
): {
  execute: (request: TRequest) => Promise<TResponse>;
  loading: boolean;
  error: string | null;
  data: TResponse | null;
  reset: () => void;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<TResponse | null>(null);

  const execute = useCallback(
    async (request: TRequest): Promise<TResponse> => {
      setLoading(true);
      setError(null);

      try {
        const fn = httpsCallable<TRequest, TResponse>(functions, functionName);
        const result = await fn(request);
        setData(result.data);
        return result.data;
      } catch (err) {
        const errorMessage = isRateLimitError(err)
          ? "You're doing a lot! Please wait a moment and try again."
          : err instanceof Error ? err.message : 'An error occurred';
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [functionName]
  );

  const reset = useCallback(() => {
    setLoading(false);
    setError(null);
    setData(null);
  }, []);

  return { execute, loading, error, data, reset };
}

/**
 * Check if an error is a rate limit error from Firebase callable functions
 */
function isRateLimitError(err: unknown): boolean {
  const error = err as { code?: string };
  return error?.code === 'functions/resource-exhausted' || error?.code === 'resource-exhausted';
}

/**
 * Hook for mutation operations (update, delete, etc.)
 */
export function useAdminMutation<TRequest, TResponse>(
  functionName: string
): {
  mutate: (request: TRequest) => Promise<TResponse>;
  loading: boolean;
  error: string | null;
  reset: () => void;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = useCallback(
    async (request: TRequest): Promise<TResponse> => {
      setLoading(true);
      setError(null);

      try {
        const fn = httpsCallable<TRequest, TResponse>(functions, functionName);
        const result = await fn(request);
        return result.data;
      } catch (err) {
        const errorMessage = isRateLimitError(err)
          ? "You're doing a lot! Please wait a moment and try again."
          : err instanceof Error ? err.message : 'An error occurred';
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [functionName]
  );

  const reset = useCallback(() => {
    setLoading(false);
    setError(null);
  }, []);

  return { mutate, loading, error, reset };
}

// ============================================================================
// Feedback Types and Hooks
// ============================================================================

export type FeedbackType = 'bug' | 'feature' | 'praise' | 'general';
export type FeedbackStatus = 'new' | 'reviewing' | 'resolved' | 'wont_fix';
export type FeedbackPriority = 'low' | 'medium' | 'high' | 'critical';

export interface FeedbackItem {
  feedbackId: string;
  userId: string;
  userEmail?: string;
  type: FeedbackType;
  message: string;
  status: FeedbackStatus;
  priority?: FeedbackPriority;
  page?: string;
  userAgent?: string;
  internalNotes?: string;
  adminResponse?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface FeedbackCounts {
  total: number;
  new: number;
  bug: number;
  feature: number;
  praise: number;
  resolved: number;
}

export interface GetFeedbackRequest {
  type?: FeedbackType;
  status?: FeedbackStatus;
  limit?: number;
  lastDocumentId?: string;
}

export interface GetFeedbackResponse {
  feedback: FeedbackItem[];
  counts: FeedbackCounts;
  lastDocumentId?: string;
}

export interface UpdateFeedbackRequest {
  feedbackId: string;
  status?: FeedbackStatus;
  priority?: FeedbackPriority;
  internalNotes?: string;
  adminResponse?: string;
}

export interface UpdateFeedbackResponse {
  success: boolean;
}

export function useGetFeedback() {
  return useAdminFunction<GetFeedbackRequest, GetFeedbackResponse>('getFeedbackHttp');
}

export function useUpdateFeedback() {
  return useAdminMutation<UpdateFeedbackRequest, UpdateFeedbackResponse>('updateFeedbackHttp');
}

// ============================================================================
// AI Activity Types and Hooks
// ============================================================================

export interface DateRangeRequest {
  startDate: string;
  endDate: string;
}

export interface AIActivitySummary {
  totalCostUsd: number;
  totalRequests: number;
  totalTokens: number;
  uniqueUsers: number;
  dateRange: { start: string; end: string };
}

export interface TaskTypeActivity {
  taskType: string;
  count: number;
  totalTokens: number;
  estimatedCostUsd: number;
  percentage: number;
}

export interface UserActivity {
  userId: string;
  email?: string;
  displayName?: string;
  totalRequests: number;
  totalTokens: number;
  totalCostUsd: number;
}

export interface DailyStats {
  date: string;
  requests: number;
  tokens: number;
  costUsd: number;
}

export interface ModelActivity {
  modelId: string;
  count: number;
  totalTokens: number;
  estimatedCostUsd: number;
  avgProcessingTimeMs: number;
}

export interface GetAIActivitySummaryResponse extends AIActivitySummary {}

export interface GetAIActivityByTaskTypeRequest extends DateRangeRequest {
  limit?: number;
}

export interface GetAIActivityByTaskTypeResponse {
  byTaskType: TaskTypeActivity[];
  taskTypes?: TaskTypeActivity[]; // alias for backward compat
}

export interface GetAIActivityByUserRequest extends DateRangeRequest {
  limit?: number;
}

export interface GetAIActivityByUserResponse {
  users: UserActivity[];
}

export interface GetAIActivityTrendResponse {
  trend: DailyStats[];
  dailyStats?: DailyStats[]; // alias for backward compat
}

export interface GetAIActivityByModelResponse {
  byModel: ModelActivity[];
  models?: ModelActivity[]; // alias for backward compat
}

export function useGetAIActivitySummary() {
  return useAdminFunction<DateRangeRequest, GetAIActivitySummaryResponse>(
    'getAIActivitySummaryHttp'
  );
}

export function useGetAIActivityByTaskType() {
  return useAdminFunction<GetAIActivityByTaskTypeRequest, GetAIActivityByTaskTypeResponse>(
    'getAIActivityByTaskTypeHttp'
  );
}

export function useGetAIActivityByUser() {
  return useAdminFunction<GetAIActivityByUserRequest, GetAIActivityByUserResponse>(
    'getAIActivityByUserHttp'
  );
}

export function useGetAIActivityTrend() {
  return useAdminFunction<DateRangeRequest, GetAIActivityTrendResponse>('getAIActivityTrendHttp');
}

export function useGetAIActivityByModel() {
  return useAdminFunction<DateRangeRequest, GetAIActivityByModelResponse>(
    'getAIActivityByModelHttp'
  );
}

// ============================================================================
// Beta Invite Types and Hooks
// ============================================================================

export interface InviteBetaUsersRequest {
  emails: string[];
  cohort?: string;
}

export interface InviteBetaUsersResponse {
  success: boolean;
  invited: string[];
  alreadyInvited: string[];
  invalid: string[];
  error?: string;
}

export function useInviteBetaUsers() {
  return useAdminMutation<InviteBetaUsersRequest, InviteBetaUsersResponse>('inviteBetaUsersHttp');
}

// ============================================================================
// Waitlist Types and Hooks
// ============================================================================

export type WaitlistStatus = 'pending' | 'invited' | 'declined' | 'expired';
export type WaitlistSource = 'website' | 'referral' | 'social' | 'direct' | 'other';

export interface WaitlistEntry {
  entryId: string;
  email: string;
  name: string;
  reason?: string;
  caregiverRole?: string;
  source: WaitlistSource;
  referralCode?: string;
  status: WaitlistStatus;
  position: number;
  createdAt: { _seconds: number; _nanoseconds: number };
  updatedAt: { _seconds: number; _nanoseconds: number };
  processedBy?: string;
  processedAt?: { _seconds: number; _nanoseconds: number };
  adminNotes?: string;
}

export interface GetWaitlistRequest {
  status?: WaitlistStatus;
  limit?: number;
  lastDocumentId?: string;
}

export interface GetWaitlistResponse {
  success: boolean;
  entries: WaitlistEntry[];
  totalCount: number;
  counts: {
    pending: number;
    invited: number;
    declined: number;
    expired: number;
  };
  lastDocumentId?: string;
  error?: string;
}

export interface UpdateWaitlistEntryRequest {
  entryId: string;
  status?: WaitlistStatus;
  adminNotes?: string;
}

export interface UpdateWaitlistEntryResponse {
  success: boolean;
  entry?: WaitlistEntry;
  error?: string;
}

export function useGetWaitlist() {
  return useAdminFunction<GetWaitlistRequest, GetWaitlistResponse>('getWaitlistHttp');
}

export function useUpdateWaitlistEntry() {
  return useAdminMutation<UpdateWaitlistEntryRequest, UpdateWaitlistEntryResponse>(
    'updateWaitlistEntryHttp'
  );
}

// ============================================================================
// Beta Users Types and Hooks
// ============================================================================

export type BetaAccessStatus = 'invited' | 'active' | 'suspended' | 'revoked';
export type EngagementStatus = 'active' | 'at_risk' | 'churned' | 'never_activated';

export interface BetaUserSummary {
  userId: string;
  email: string;
  displayName?: string;
  status: BetaAccessStatus;
  cohort?: string;
  invitedAt: { _seconds: number; _nanoseconds: number };
  activatedAt?: { _seconds: number; _nanoseconds: number };
  lastActivityAt?: { _seconds: number; _nanoseconds: number };
  engagementStatus: EngagementStatus;
  caseFileCount: number;
}

export interface GetBetaUsersRequest {
  status?: BetaAccessStatus;
  cohort?: string;
  engagementStatus?: EngagementStatus;
  limit?: number;
  lastDocumentId?: string;
}

export interface GetBetaUsersResponse {
  success: boolean;
  users: BetaUserSummary[];
  counts: {
    total: number;
    active: number;
    atRisk: number;
    churned: number;
    neverActivated: number;
  };
  lastDocumentId?: string;
  error?: string;
}

export interface UpdateBetaUserRequest {
  userId: string;
  status?: BetaAccessStatus;
  statusReason?: string;
  adminNotes?: string;
  cohort?: string;
}

export interface UpdateBetaUserResponse {
  success: boolean;
  error?: string;
}

export function useGetBetaUsers() {
  return useAdminFunction<GetBetaUsersRequest, GetBetaUsersResponse>('getBetaUsersHttp');
}

export function useUpdateBetaUser() {
  return useAdminMutation<UpdateBetaUserRequest, UpdateBetaUserResponse>('updateBetaUserHttp');
}
